import { LazyPreviewService } from 'src/app/services/lazy-preview.service';
import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Subscription, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { DocumentService } from 'src/app/modules/document/document.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { ConfirmationPopupComponent } from 'src/app/modules/shared/confirmation-popup/confirmation-popup.component';
import { DocumentImageAnnotatorComponent } from 'src/app/modules/document/document-image-annotator/document-image-annotator.component';
import { VideoPreviewComponent } from 'src/app/modules/shared/video-preview/video-preview.component';
import { AudioPreviewComponent } from 'src/app/modules/shared/audio-preview/audio-preview.component';
import { PdfPreviewComponent } from 'src/app/modules/shared/pdf-preview/pdf-preview.component';
import { ALLOWED_UPLOAD_EXTENSIONS } from 'src/app/modules/shared/upload-file-types.constants';

interface UploadFile {
  id: string;
  file: File;
  fileName: string;
  fileSize: string;
  fileSizeBytes: number;
  fileType: string;
  status: 'pending' | 'uploading' | 'complete' | 'error' | 'cancelled';
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
  preview: string | null;
  uploadedId?: number;
  uploadedUrl?: string;
  error?: string;
  startTime?: number;
  endTime?: number;
  uploadSpeed?: number;
  timeRemaining?: number;
  retryCount: number;
  abortController?: AbortController;
}

@Component({
    selector: 'app-ticket-attachments',
    templateUrl: './ticket-attachments.component.html',
    styleUrls: ['./ticket-attachments.component.scss'],
    animations: [
        trigger('slideInFromTop', [
            transition(':enter', [
                style({ transform: 'translateY(-100%)', opacity: 0 }),
                animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateY(-100%)', opacity: 0 }))
            ])
        ]),
        trigger('zoomIn', [
            transition(':enter', [
                style({ transform: 'scale(0.95)', opacity: 0 }),
                animate('200ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ transform: 'scale(0.95)', opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class TicketAttachmentsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly lazyPreview = inject(LazyPreviewService);
  @Input() module_id: number = 8; // Ticket module ID
  @Input() attachable_id: string = ''; // Ticket ID
  @Input() isLocked: boolean = false; // Lock state - when true, uploads/deletes are disabled

  // Dynamic folder ID fetched from event-folder API
  private folder_id: number | null = null;

  // Track loaded ticket to prevent duplicate API calls
  private hasLoadedForTicket: string | null = null;

  // Drag & drop state
  isDragging = false;
  dragCounter = 0;
  draggedFileCount = 0;

  // Upload queue
  uploadQueue: UploadFile[] = [];
  uploadProgress: UploadFile[] = [];
  currentUploads = new Map<string, Subscription>();
  isUploadPanelOpen = false;

  // Documents list
  documents: any[] = [];
  isLoading = false;

  // View mode
  viewMode: 'grid' | 'list' = 'grid';
  tableView = false; // Backward compatibility

  // File validation
  MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
  MAX_CONCURRENT_UPLOADS = 3;
  ALLOWED_TYPES = ALLOWED_UPLOAD_EXTENSIONS;

  constructor(
    private http: HttpClient,
    private toasterService: ToasterService,
    private backendApiService: BackendApiService,
    private documentService: DocumentService,
    private dialog: NgDialogAnimationService
  ) {}

  ngOnInit(): void {
    // Fetch dynamic folder first, then load documents
    if (this.attachable_id) {
      this.hasLoadedForTicket = this.attachable_id;
      this.getEventFolderForTicket();
    } else {
      console.warn('Ticket attachments: attachable_id (ticket ID) is required');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if attachable_id changed and is now available
    if (changes['attachable_id'] && changes['attachable_id'].currentValue) {
      const newTicketId = changes['attachable_id'].currentValue;

      // Only load if this is a different ticket or first load
      if (this.hasLoadedForTicket !== newTicketId) {
        console.log('Ticket ID changed to:', newTicketId);
        this.hasLoadedForTicket = newTicketId;

        // Reset folder_id and documents
        this.folder_id = null;
        this.documents = [];

        // Fetch folder for new ticket
        this.getEventFolderForTicket();
      }
    }
  }

  ngOnDestroy(): void {
    // Cancel all ongoing uploads
    this.currentUploads.forEach(sub => sub.unsubscribe());
    this.currentUploads.clear();
  }

  /**
   * Get or create the event folder for ticket attachments
   * Uses event: 'save_document' to match backend expectations
   */
  private getEventFolderForTicket(): void {
    const eventFolderParams = {
      module_id: this.module_id,
      attachable_id: this.attachable_id,
      event: 'save_document'
    };

    console.log('Fetching event folder for ticket with params:', eventFolderParams);

    this.documentService.getEventFolder(eventFolderParams).subscribe({
      next: (response: any) => {
        console.log('Event folder API response:', response);

        // Extract folder_id from nested response structure
        // Response structure: { data: { success, message, data: { id, ... } } }
        const folderId = response?.data?.data?.id ||
                        response?.data?.data?.data?.id ||
                        response?.data?.folder_id ||
                        response?.folder_id;

        if (folderId) {
          this.folder_id = folderId;
          console.log(`✅ Ticket attachment folder ID retrieved: ${folderId}`);

          // Now load documents for this folder
          this.loadDocuments();
        } else {
          console.warn('No folder_id returned from event-folder API. Response:', response);
          this.toasterService.showError('', 'Failed to get attachment folder');
          this.isLoading = false;
        }
      },
      error: (err: any) => {
        // A 404 is the documented benign case: the record has no "save_document"
        // folder yet, which is normal for a ticket that has never had an attachment.
        // http-error.interceptor already suppresses its own toast for this endpoint;
        // showing one here re-surfaced the same expected 404 as a user-facing error.
        if (err?.status === 404) {
          this.isLoading = false;
          return;
        }
        console.error('Error getting event folder:', err);
        this.toasterService.showError('', 'Failed to initialize attachments');
        this.isLoading = false;
      }
    });
  }

  /**
   * Load documents for current ticket using dynamic folder ID
   */
  private loadDocuments(): void {
    if (!this.folder_id) {
      console.warn('Cannot load documents: folder_id not available');
      return;
    }

    this.isLoading = true;

    // Build query params - use correct API format
    const params = new URLSearchParams({
      module_id: this.module_id.toString(),
      folder_id: this.folder_id.toString(),
      page: '1',
      auto_parent: 'true'
    });

    this.backendApiService.getApi(`document?${params.toString()}`).subscribe({
      next: (response: any) => {
        // API response structure: response.data.data.documents.list
        console.log('Documents API Response:', response);
        this.documents = response?.data?.data?.documents?.list || [];
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error loading documents:', err);
        this.documents = [];
        this.isLoading = false;
      }
    });
  }

  /**
   * ============================================================================
   * DRAG & DROP HANDLERS
   * ============================================================================
   */

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDragging = true;

    if (event.dataTransfer?.items) {
      this.draggedFileCount = event.dataTransfer.items.length;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;

    if (this.dragCounter === 0) {
      this.isDragging = false;
      this.draggedFileCount = 0;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.dragCounter = 0;
    this.draggedFileCount = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files);
    }
  }

  /**
   * ============================================================================
   * FILE UPLOAD HANDLERS
   * ============================================================================
   */

  /**
   * Handle file selection from file input
   */
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files);
    }
    // Reset input value to allow selecting same file again
    event.target.value = '';
  }

  /**
   * Handle file upload with validation and queue management
   */
  async handleFileUpload(files: FileList | File[]): Promise<void> {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.toasterService.showError('', validation.error || 'Invalid file');
        continue;
      }

      // Check for duplicates
      if (this.isDuplicateFile(file)) {
        this.toasterService.showWarning('', `${file.name} is already in upload queue`);
        continue;
      }

      // Generate preview for images
      const preview = await this.generatePreview(file);

      // Create upload file object
      const uploadFile: UploadFile = {
        id: this.generateUUID(),
        file: file,
        fileName: file.name,
        fileSize: this.formatFileSize(file.size),
        fileSizeBytes: file.size,
        fileType: file.name.split('.').pop()?.toLowerCase() || '',
        status: 'pending',
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        preview: preview,
        retryCount: 0,
        abortController: new AbortController()
      };

      // Add to queue
      this.uploadQueue.push(uploadFile);
    }

    // Update reference for template
    this.uploadProgress = this.uploadQueue;
    this.isUploadPanelOpen = true;

    // Process upload queue
    this.processUploadQueue();
  }

  /**
   * Validate file before upload
   */
  private validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${this.formatFileSize(this.MAX_FILE_SIZE)} limit`
      };
    }

    // Check file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.ALLOWED_TYPES.includes(ext)) {
      return {
        valid: false,
        error: `File type .${ext} is not supported`
      };
    }

    return { valid: true };
  }

  /**
   * Check if file already exists in upload queue
   */
  private isDuplicateFile(file: File): boolean {
    return this.uploadQueue.some(
      item =>
        item.fileName === file.name &&
        item.fileSizeBytes === file.size &&
        item.status !== 'error' &&
        item.status !== 'cancelled'
    );
  }

  /**
   * Process upload queue with concurrent limit
   */
  private processUploadQueue(): void {
    const pending = this.uploadQueue.filter(f => f.status === 'pending');
    const uploading = this.uploadQueue.filter(f => f.status === 'uploading').length;

    // Start uploads up to concurrent limit
    const available = this.MAX_CONCURRENT_UPLOADS - uploading;
    const toStart = pending.slice(0, available);

    toStart.forEach(uploadFile => {
      this.startUpload(uploadFile);
    });
  }

  /**
   * Start individual file upload
   */
  private startUpload(uploadFile: UploadFile): void {
    // Ensure folder_id is available before uploading
    if (!this.folder_id) {
      console.error('Cannot upload: folder_id not available');
      this.handleUploadError(uploadFile, { message: 'Folder not initialized' });
      return;
    }

    uploadFile.status = 'uploading';
    uploadFile.startTime = Date.now();

    // Prepare upload parameters with dynamic folder_id
    const uploadParams = {
      module_id: this.module_id,
      folder_id: this.folder_id,
      attachable_id: this.attachable_id ? parseInt(this.attachable_id) : undefined,
      attachment_field: 'document'
    };

    // Start upload with progress tracking
    const uploadSubscription = this.documentService
      .uploadFileWithProgress(uploadFile.file, uploadParams, false)
      .pipe(
        catchError((error) => {
          this.handleUploadError(uploadFile, error);
          return EMPTY;
        })
      )
      .subscribe((event: HttpEvent<any>) => {
        this.handleUploadEvent(uploadFile, event);
      });

    // Store subscription for cancellation
    this.currentUploads.set(uploadFile.id, uploadSubscription);
  }

  /**
   * Handle upload progress events
   */
  private handleUploadEvent(uploadFile: UploadFile, event: HttpEvent<any>): void {
    if (event.type === HttpEventType.UploadProgress && event.total) {
      // Calculate progress
      uploadFile.uploadedBytes = event.loaded;
      uploadFile.totalBytes = event.total;
      uploadFile.progress = Math.round((100 * event.loaded) / event.total);

      // Calculate speed and ETA
      const elapsedTime = (Date.now() - (uploadFile.startTime || Date.now())) / 1000; // seconds
      uploadFile.uploadSpeed = elapsedTime > 0 ? event.loaded / elapsedTime : 0;

      const remainingBytes = event.total - event.loaded;
      uploadFile.timeRemaining = uploadFile.uploadSpeed > 0
        ? remainingBytes / uploadFile.uploadSpeed
        : 0;

    } else if (event.type === HttpEventType.Response) {
      // Upload complete
      this.handleUploadComplete(uploadFile, event.body);
    }
  }

  /**
   * Handle successful upload completion
   */
  private handleUploadComplete(uploadFile: UploadFile, response: any): void {
    uploadFile.status = 'complete';
    uploadFile.progress = 100;
    uploadFile.endTime = Date.now();
    uploadFile.uploadedId = response?.data?.id;
    uploadFile.uploadedUrl = response?.data?.file_url;

    // Remove from active uploads
    this.currentUploads.delete(uploadFile.id);

    // Show success message
    this.toasterService.showSuccess('', `${uploadFile.fileName} uploaded successfully`);

    // Reload documents list
    this.loadDocuments();

    // Continue processing queue
    this.processUploadQueue();
  }

  /**
   * Handle upload error
   */
  private handleUploadError(uploadFile: UploadFile, error: any): void {
    uploadFile.status = 'error';
    uploadFile.error = error?.message || 'Upload failed';

    // Remove from active uploads
    this.currentUploads.delete(uploadFile.id);

    this.toasterService.showError('', `Failed to upload ${uploadFile.fileName}`);

    // Continue processing queue
    this.processUploadQueue();
  }

  /**
   * Cancel upload
   */
  cancelUpload(uploadFile: UploadFile): void {
    if (uploadFile.abortController) {
      uploadFile.abortController.abort();
    }

    const subscription = this.currentUploads.get(uploadFile.id);
    if (subscription) {
      subscription.unsubscribe();
      this.currentUploads.delete(uploadFile.id);
    }

    uploadFile.status = 'cancelled';
    this.toasterService.showInfo('', `Upload cancelled: ${uploadFile.fileName}`);

    // Continue processing queue
    this.processUploadQueue();
  }

  /**
   * Retry failed upload
   */
  retryUpload(uploadFile: UploadFile): void {
    uploadFile.status = 'pending';
    uploadFile.progress = 0;
    uploadFile.uploadedBytes = 0;
    uploadFile.error = undefined;
    uploadFile.retryCount++;
    uploadFile.abortController = new AbortController();

    this.processUploadQueue();
  }

  /**
   * Remove upload from queue
   */
  removeFromQueue(uploadFile: UploadFile): void {
    const index = this.uploadQueue.findIndex(f => f.id === uploadFile.id);
    if (index > -1) {
      // Cancel if uploading
      if (uploadFile.status === 'uploading') {
        this.cancelUpload(uploadFile);
      }

      this.uploadQueue.splice(index, 1);
      this.uploadProgress = [...this.uploadQueue];
    }
  }

  /**
   * Clear completed uploads from queue
   */
  clearCompleted(): void {
    this.uploadQueue = this.uploadQueue.filter(f => f.status !== 'complete');
    this.uploadProgress = [...this.uploadQueue];
  }

  /**
   * Clear all uploads from queue
   */
  clearAll(): void {
    // Cancel any active uploads
    this.uploadQueue.forEach(file => {
      if (file.status === 'uploading') {
        this.cancelUpload(file);
      }
    });

    this.uploadQueue = [];
    this.uploadProgress = [];
  }

  /**
   * ============================================================================
   * DELETE HANDLERS (using shared ConfirmationPopupComponent)
   * ============================================================================
   */

  /**
   * Open delete confirmation (using shared confirmation popup)
   */
  confirmDelete(document: any): void {
    const message = `Are you sure you want to delete "${document.original_name || document.name}"?`;
    const description = 'This action cannot be undone. The file will be permanently deleted.';

    const data = {
      message,
      description,
      model: []
    };

    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.executeDelete(document);
      }
    });
  }

  /**
   * Execute delete
   */
  private executeDelete(document: any): void {
    // Use DocumentService to delete document (same as document manager)
    this.documentService.deleteDocument(document.id).subscribe({
      next: (res: any) => {
        this.toasterService.showSuccessMessage(res?.data?.message);

        // Reload documents list
        this.loadDocuments();
      },
      error: (err: any) => {
        this.toasterService.showError('', 'Failed to delete file');
        console.error('Delete error:', err);
      }
    });
  }

  /**
   * ============================================================================
   * VIEW MODE METHODS
   * ============================================================================
   */

  /**
   * Toggle between grid and list view
   */
  toggleTableView(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    this.tableView = this.viewMode === 'list';
  }

  /**
   * Set view mode
   */
  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
    this.tableView = mode === 'list';
  }

  /**
   * ============================================================================
   * UTILITY METHODS
   * ============================================================================
   */

  /**
   * Format file size to human-readable string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Generate UUID for unique upload IDs
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Generate preview for image files
   */
  private async generatePreview(file: File): Promise<string | null> {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    if (!imageTypes.includes(ext)) {
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        resolve(e.target.result);
      };
      reader.onerror = () => {
        resolve(null);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get file icon based on extension
   */
  getFileIcon(document: any): string {
    const ext = (document.file_type || document.extension || '').toLowerCase();

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return 'fa-file-image';
    }
    // PDF
    if (ext === 'pdf') {
      return 'fa-file-pdf';
    }
    // Office - Word
    if (['doc', 'docx'].includes(ext)) {
      return 'fa-file-word';
    }
    // Office - Excel
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return 'fa-file-excel';
    }
    // Office - PowerPoint
    if (['ppt', 'pptx'].includes(ext)) {
      return 'fa-file-powerpoint';
    }
    // Video
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) {
      return 'fa-file-video';
    }
    // Audio
    if (['mp3', 'wav', 'ogg'].includes(ext)) {
      return 'fa-file-audio';
    }
    // Archive
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return 'fa-file-archive';
    }
    // Text
    if (['txt', 'rtf'].includes(ext)) {
      return 'fa-file-alt';
    }

    // Default
    return 'fa-file';
  }

  /**
   * Get file type badge color
   */
  getFileTypeColor(document: any): string {
    const ext = (document.file_type || document.extension || '').toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
    if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
    if (['zip', 'rar', '7z'].includes(ext)) return 'archive';

    return 'default';
  }

  /**
   * Format time remaining
   */
  formatTimeRemaining(seconds: number): string {
    if (!seconds || seconds <= 0) return '';
    if (seconds < 60) return `${Math.round(seconds)}s remaining`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m remaining`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m remaining`;
  }

  /**
   * Format upload speed
   */
  formatUploadSpeed(bytesPerSecond: number): string {
    if (!bytesPerSecond || bytesPerSecond <= 0) return '';
    const mbps = bytesPerSecond / (1024 * 1024);
    if (mbps < 1) {
      const kbps = bytesPerSecond / 1024;
      return `${kbps.toFixed(1)} KB/s`;
    }
    return `${mbps.toFixed(1)} MB/s`;
  }

  /**
   * Toggle upload panel
   */
  toggleUploadPanel(): void {
    this.isUploadPanelOpen = !this.isUploadPanelOpen;
  }

  /**
   * Get count of currently uploading files
   */
  getUploadingCount(): number {
    return this.uploadQueue.filter(u => u.status === 'uploading').length;
  }

  /**
   * Check if any uploads are complete
   */
  hasCompletedUploads(): boolean {
    return this.uploadQueue.some(u => u.status === 'complete');
  }

  /**
   * ============================================================================
   * PREVIEW FUNCTIONALITY
   * ============================================================================
   */

  /**
   * Open preview modal based on file type (matching document manager)
   */
  openPreview(document: any): void {
    const fileType = (document.file_type || document.extension || '').toLowerCase();
    const { file_url, ...rest } = document;
    const modifiedModel = { ...rest, url: file_url };

    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'].includes(fileType)) {
      this.dialog.open(DocumentImageAnnotatorComponent, {
        width: '92vw',
        maxWidth: '92vw',
        height: '88vh',
        data: modifiedModel,
        panelClass: 'document-image-annotator-panel'
      });
    }
    // Video files
    else if (['mp4', 'mov', 'mkv', 'wmv', 'mpg', 'mpeg', 'avi', 'webm'].includes(fileType)) {
      this.dialog.open(VideoPreviewComponent, {
        width: '80%',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
    }
    // Audio files
    else if (['mp3', 'wav', 'aac', 'wma', 'ogg', 'm4a'].includes(fileType)) {
      this.dialog.open(AudioPreviewComponent, {
        width: '600px',
        maxWidth: '90vw',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
    }
    // PDF files
    else if (fileType === 'pdf') {
      this.dialog.open(PdfPreviewComponent, {
        width: '90%',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
    }
    // Text files
    else if (['txt', 'log', 'json', 'xml', 'csv', 'md'].includes(fileType)) {
      this.lazyPreview.openTextPreview({
        width: '80%',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
    }
    // Unsupported - download instead
    else {
      this.downloadFile(document);
    }
  }

  /**
   * Download file
   */
  downloadFile(document: any): void {
    if (document.file_url) {
      window.open(document.file_url, '_blank');
      this.toasterService.showInfo('', `Downloading ${document.original_name || document.name}`);
    } else {
      this.toasterService.showError('', 'File URL not available');
    }
  }

  /**
   * Get image URL or icon for display
   */
  getImageIcon(document: any): string {
    const fileType = (document.file_type || document.extension || '').toLowerCase();

    // For images, show actual thumbnail if available
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileType)) {
      if (document.file_url) {
        return document.file_url;
      }
      // If thumbnail URL exists, use it
      if (document.thumbnail_url) {
        return document.thumbnail_url;
      }
    }

    // For PDFs, use thumbnail if available
    if (fileType === 'pdf' && document.thumbnail_url) {
      return document.thumbnail_url;
    }

    // For videos, use poster/thumbnail if available
    if (['mp4', 'mov', 'mkv', 'wmv', 'avi', 'webm'].includes(fileType)) {
      if (document.video_poster || document.thumbnail_url) {
        return document.video_poster || document.thumbnail_url;
      }
    }

    // Fall back to default icon (handled by CSS)
    return '';
  }

  /**
   * Handle image load error - show icon instead
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const icon = img.nextElementSibling as HTMLElement;

    if (img) {
      img.style.display = 'none';
    }

    if (icon) {
      icon.style.display = 'block';
    }
  }
}
